import { useRef } from "react";
import { motion, useInView } from "framer-motion";
import {
    Globe,
    Smartphone,
    Database,
    Brain,
    Palette,
    Server,
    HeadphonesIcon,
    ArrowUpRight,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { useLanguage } from "@/contexts/LanguageContext";
import { useHorizontalRail } from "./rail/useHorizontalRail";
import {
    RailArrows,
    RailEdgeFades,
    RailEyebrow,
    RailPagination,
    RailProgressBar,
    RailScrollbarHideStyle,
    railContainerClass,
} from "./rail/RailControls";

interface Service {
    num: string;
    icon: React.ReactNode;
    key: string;
    tags?: string[];
    accentHue: number;
}

const SERVICES: Service[] = [
    {
        num: "01",
        icon: <Globe className="w-7 h-7" />,
        key: "web",
        tags: ["NEXT.JS", "REACT", "TYPESCRIPT", "POSTGRES", "PRISMA", "REDIS", "TRPC", "TAILWIND"],
        accentHue: 260,
    },
    {
        num: "02",
        icon: <Smartphone className="w-7 h-7" />,
        key: "mobile",
        tags: ["FLUTTER", "REACT NATIVE", "SWIFT", "KOTLIN", "EXPO", "FIREBASE", "PUSH", "OFFLINE"],
        accentHue: 190,
    },
    {
        num: "03",
        icon: <Database className="w-7 h-7" />,
        key: "erp",
        tags: ["ERP", "CRM", "POS", "METABASE", "KAFKA", "ETL", "OLAP", "POSTGRES"],
        accentHue: 280,
    },
    {
        num: "04",
        icon: <Brain className="w-7 h-7" />,
        key: "ai",
        tags: ["OPENAI", "CLAUDE", "GEMINI", "RAG", "EMBEDDINGS", "MCP", "VECTOR DB", "LANGCHAIN", "AGENTS", "FINE-TUNE", "WHISPER", "VISION"],
        accentHue: 220,
    },
    {
        num: "05",
        icon: <Palette className="w-7 h-7" />,
        key: "branding",
        tags: ["FIGMA", "ILLUSTRATOR", "AFTER EFFECTS", "MOTION", "3D", "PROTOTYPING", "DESIGN SYSTEM"],
        accentHue: 320,
    },
    {
        num: "06",
        icon: <Server className="w-7 h-7" />,
        key: "hosting",
        tags: ["AWS", "CLOUDFLARE", "VERCEL", "DOCKER", "K8S", "TERRAFORM", "CI/CD", "SSL", "CDN"],
        accentHue: 160,
    },
    {
        num: "07",
        icon: <HeadphonesIcon className="w-7 h-7" />,
        key: "support",
        tags: ["24/7", "SLA", "SENTRY", "DATADOG", "GRAFANA", "PAGERDUTY", "tag_partnership"],
        accentHue: 30,
    },
];

const ServiceCard = ({
    service,
    index,
    total,
    staggerInView,
}: {
    service: Service;
    index: number;
    total: number;
    staggerInView: boolean;
}) => {
    const { t } = useTranslation();
    const accent = `hsl(${service.accentHue} 90% 60%)`;
    const accentDim = `hsl(${service.accentHue} 90% 60% / 0.35)`;
    const accentSoft = `hsl(${service.accentHue} 90% 60% / 0.18)`;

    return (
        <motion.div
            className="group relative h-full"
            initial={{ opacity: 0, y: 30 }}
            animate={staggerInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
            transition={{
                duration: 0.55,
                delay: 0.08 * index,
                ease: [0.22, 1, 0.36, 1],
            }}
            whileHover={{ y: -4 }}
        >
            <div
                aria-hidden
                className="absolute -inset-px rounded-[32px] opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
                style={{
                    background: `conic-gradient(from 140deg at 50% 50%, ${accent}, transparent 35%, transparent 65%, ${accent})`,
                    padding: "1px",
                    WebkitMask:
                        "linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)",
                    WebkitMaskComposite: "xor",
                    mask: "linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)",
                    maskComposite: "exclude",
                }}
            />

            <div
                className="glass-v2 rounded-[32px] p-7 md:p-8 relative overflow-hidden h-full flex flex-col"
                data-cursor="hover"
            >
                <span
                    aria-hidden
                    className="absolute -top-4 end-4 font-v2-display leading-none select-none pointer-events-none text-[120px] md:text-[140px] text-white/[0.04] tracking-tighter"
                >
                    {service.num}
                </span>

                <div
                    aria-hidden
                    className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
                    style={{
                        background: `radial-gradient(600px circle at 50% 0%, ${accentSoft}, transparent 60%)`,
                    }}
                />

                <div
                    aria-hidden
                    className="absolute top-0 inset-x-8 h-px opacity-60 pointer-events-none"
                    style={{
                        background: `linear-gradient(90deg, transparent, ${accent}, transparent)`,
                    }}
                />

                <div className="relative flex-1 flex flex-col">
                    <div className="flex items-start gap-4 mb-5">
                        <div className="relative shrink-0">
                            <div
                                aria-hidden
                                className="absolute -inset-1.5 rounded-[20px] opacity-0 group-hover:opacity-60 transition-opacity duration-500 blur-md"
                                style={{ background: accent }}
                            />
                            <div
                                className="relative w-14 h-14 rounded-2xl flex items-center justify-center text-white transition-transform duration-500 group-hover:scale-110"
                                style={{
                                    background: `linear-gradient(145deg, ${accent}, hsl(${service.accentHue} 90% 45%))`,
                                    boxShadow: `0 0 32px ${accentSoft}, inset 0 1px 0 hsl(${service.accentHue} 90% 80% / 0.4)`,
                                }}
                            >
                                {service.icon}
                            </div>
                        </div>

                        <div className="flex-1 min-w-0 pt-1">
                            <div className="flex items-center gap-2 mb-1.5">
                                <span
                                    className="font-v2-label text-[9px] px-2 py-0.5 rounded-full border"
                                    style={{
                                        background: accentSoft,
                                        borderColor: accentDim,
                                        color: `hsl(${service.accentHue} 90% 85%)`,
                                    }}
                                >
                                    {service.num}
                                </span>
                                <span className="font-v2-label text-[10px] text-white/40">
                                    {t("services.step_label", { num: service.num })}
                                </span>
                            </div>
                            <h3 className="font-v2-display text-lg md:text-xl text-white leading-tight">
                                {t(`services.items.${service.key}.title`)}
                            </h3>
                        </div>
                    </div>

                    <p className="text-sm md:text-[15px] text-muted-foreground leading-relaxed font-medium">
                        {t(`services.items.${service.key}.desc`)}
                    </p>

                    {service.tags && service.tags.length > 0 && (
                        <div className="mt-5 flex flex-wrap gap-1.5">
                            {service.tags.map((tag) => (
                                <span
                                    key={tag}
                                    className="font-v2-label text-[10px] px-2.5 py-0.5 rounded-full border transition-all duration-300"
                                    style={{
                                        background: accentSoft,
                                        borderColor: accentDim,
                                        color: `hsl(${service.accentHue} 90% 85%)`,
                                    }}
                                >
                                    {tag === "tag_partnership"
                                        ? t("services.items.tag_partnership")
                                        : tag}
                                </span>
                            ))}
                        </div>
                    )}

                    <div className="mt-auto pt-6 flex items-center justify-between">
                        <div className="flex items-center gap-2.5 min-w-0">
                            <span className="font-v2-label text-[10px] text-white/50 tabular-nums">
                                {String(index + 1).padStart(2, "0")}
                                <span className="text-white/20 mx-1">/</span>
                                {String(total).padStart(2, "0")}
                            </span>
                            <div className="relative h-px w-10 group-hover:w-20 transition-[width] duration-500 overflow-hidden">
                                <div className="absolute inset-0 bg-white/10" />
                                <div
                                    className="absolute inset-y-0 start-0 w-0 group-hover:w-full transition-[width] duration-700 ease-out"
                                    style={{ background: accent }}
                                />
                            </div>
                        </div>
                        <div
                            className="w-9 h-9 rounded-full flex items-center justify-center text-white transition-all duration-500 group-hover:scale-110 group-hover:rotate-45"
                            style={{
                                background: accentSoft,
                                border: `1px solid ${accentDim}`,
                            }}
                        >
                            <ArrowUpRight size={14} />
                        </div>
                    </div>
                </div>
            </div>
        </motion.div>
    );
};

const ServicesTimeline = () => {
    const { t } = useTranslation();
    const { dir } = useLanguage();
    const isRtl = dir === "rtl";
    const sectionRef = useRef<HTMLElement>(null);
    const staggerInView = useInView(sectionRef, { amount: 0.15, margin: "-40px" });

    const {
        railRef,
        canPrev,
        canNext,
        needsScroll,
        activeIndex,
        scrollProgress,
        scrollToIndex,
        scrollByCard,
    } = useHorizontalRail({
        itemCount: SERVICES.length,
        cardSelector: "[data-service-card]",
        isRtl,
    });

    return (
        <section ref={sectionRef} className="py-20 md:py-32 relative" id="services">
            <div className="container mx-auto px-6 relative">
                <motion.div
                    className="max-w-3xl mx-auto text-center mb-12 md:mb-16"
                    initial={{ opacity: 0, y: 40 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ amount: 0.3, margin: "-80px" }}
                    transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                >
                    <RailEyebrow>{t("services.eyebrow")}</RailEyebrow>
                    <h2 className="font-v2-display text-4xl md:text-6xl text-white mb-6">
                        {t("services.title_prefix")}{" "}
                        <span className="bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
                            {t("services.title_highlight")}
                        </span>
                    </h2>
                    <p className="text-lg text-muted-foreground leading-relaxed font-medium">
                        {t("services.subtitle")}
                    </p>
                </motion.div>

                <RailPagination
                    itemCount={SERVICES.length}
                    activeIndex={activeIndex}
                    onSelect={scrollToIndex}
                    accentHues={SERVICES.map((s) => s.accentHue)}
                    ariaLabelPrefix="service"
                />
            </div>

            <motion.div
                className="relative"
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ amount: 0.15, margin: "-40px" }}
                transition={{ duration: 0.7, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
            >
                {needsScroll && (
                    <>
                        <RailEdgeFades isRtl={isRtl} />
                        <RailArrows
                            onPrev={() => scrollByCard(-1)}
                            onNext={() => scrollByCard(1)}
                            canPrev={canPrev}
                            canNext={canNext}
                            isRtl={isRtl}
                        />
                    </>
                )}

                <div
                    ref={railRef}
                    tabIndex={needsScroll ? 0 : -1}
                    aria-label="Services carousel"
                    className={railContainerClass(needsScroll)}
                >
                    {SERVICES.map((service, i) => (
                        <div
                            key={service.num}
                            data-service-card
                            className="snap-center shrink-0 w-[85vw] sm:w-[60vw] md:w-[320px] xl:w-[340px]"
                        >
                            <ServiceCard
                                service={service}
                                index={i}
                                total={SERVICES.length}
                                staggerInView={staggerInView}
                            />
                        </div>
                    ))}
                </div>

                {needsScroll && <RailProgressBar progress={scrollProgress} />}
            </motion.div>

            <RailScrollbarHideStyle />
        </section>
    );
};

export default ServicesTimeline;
